import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { AreaTypeEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { FormArray, FormGroup } from '@angular/forms';
import { TenantDto } from '@bo-client/shared/models/tenantDto';
import { AreaTypesModel } from '@bo-client/shared/models/areaTypes';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { DevelopmentAreaInfo } from '@bo-client/shared/models/developmentAreas';
import { UserDto } from '@bo-client/shared/models/userDto';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ErrorsFormsService } from '@bo-client/shared/form-errors/errors-forms.service';
import { map, takeUntil } from 'rxjs/operators';
import { uri } from '@bo-client/shared/constants/uri';
import {
  GridRegistrationStatusEnum,
  GridRegistrationTypeEnum,
  PowerGenerationSourceEnum
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import { ProducerModel } from '@bo-client/activation-management/grid-registration/model/producer.model';
import { PVModuleModel } from '@bo-client/activation-management/grid-registration/model/pv-module.model';
import { GridRegistrationRequestService } from '@bo-client/activation-management/grid-registration/grid-registration-request.service';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { ConsumerModel } from '@bo-client/activation-management/grid-registration/model/consumer.model';
import { MeteringConceptModel } from '@bo-client/activation-management/shared/models/meteringConceptModel';
import { ActionWithMoreInfoModel } from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import {
  AnswerModalTypeEnum,
  DisplayAndSelectModel
} from '@bo-client/shared/components/display-and-selec-option/display-and-select.model';
import { DisplayAndSelectService } from '@bo-client/shared/components/display-and-selec-option/display-and-select.service';
import { CompareEnum, DocumentTagEnum } from '@bo-client/shared/constants/enums';
import { LoadDataFormsGridRegistrationService } from '@bo-client/activation-management/shared/services/load-data-forms-grid-registration.service';
import { CommissioningFormsErrorsService } from '@bo-client/activation-management/commissioning/forms/commissioning-forms-errors.service';
import { CreateGridRegistrationFormsService } from '@bo-client/activation-management/grid-registration/create-grid-registration-forms.service';
import { ErrorFormCommissioningEnum } from '@bo-client/activation-management/shared/forms/create-forms-errors-model';
import { RequestFormBase } from '@bo-client/shared/base-components/request-form.base';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { ShowErrorModel } from '@bo-client/shared/form-errors/errors-form-model';

@Component({
  selector: 'app-grid-registration-form',
  templateUrl: './grid-registration-form.component.html',
  styleUrls: ['./grid-registration-form.component.scss']
})
export class GridRegistrationFormComponent
  extends RequestFormBase<GridRegistrationModel>
  implements OnInit, OnDestroy
{
  @ViewChild('formContainer')
  formContainerRef: ElementRef;

  @Output() outToParent = new EventEmitter<any>();
  @Input() eventsFromParent: Observable<ActionWithMoreInfoModel>;

  @Output()
  onErrorsChanged = new EventEmitter<any>();

  getAllValidationErrors(): ShowErrorModel[] {
    return this.errorFormList;
  }

  public eventsToGis: Subject<string> = new Subject<string>();

  public areaType: AreaTypeEnum = AreaTypeEnum.POWER;
  public documentTagEnum = DocumentTagEnum;
  public compareEnum = CompareEnum;

  public gridRegistration: GridRegistrationModel = new GridRegistrationModel();
  public selectedTenant: TenantDto;

  public areaTypeList: AreaTypesModel[];
  public companyLocationsBytenant: CompanyLocationDto[];
  public listDevelopmentAreas: DevelopmentAreaInfo[];
  public technician: UserDto;

  public showGetGisData = true;

  public hasPvModule = false;

  public selectedWishDateOK = true;
  public selectedWishPartDateOK = true;

  public gridRegistrationStatusEnum = GridRegistrationStatusEnum;

  public feederTypeEnum = PowerGenerationSourceEnum;

  public listInverterDevices: Array<ProducerModel> = [];
  public listProducers: Array<ProducerModel> = [];
  public listConsumers: Array<ConsumerModel> = [];
  public hasPowerInverter = false;
  public hasProducers = false;

  public meteringConceptList: MeteringConceptModel[];
  public meteringListToShow: DisplayAndSelectModel[];
  public selectedMeteringConceptToShow: DisplayAndSelectModel;

  public storageConceptList: MeteringConceptModel[];
  public storageListToShow: DisplayAndSelectModel[];
  public selectedStorageConceptToShow: DisplayAndSelectModel;

  public gridRegistrationTypeEnum = GridRegistrationTypeEnum;
  public gridRegistrationType: GridRegistrationTypeEnum;

  public totalPowerProducers = 0;
  public totalPowerPVModules = 0;

  public documentRequired = false;

  public maxExtractionPower = null;
  public canUpdate = false;
  public disableAllFields = false;
  public disableConditions = false;
  public formGroup: FormGroup;

  hideAdditionalGridConnectionPointInformationCard$: Observable<boolean> = of(false);

  constructor(
    protected authorizationService: AuthorizationService,
    protected httpHapService: HttpHapService,
    protected dataShareService: DataShareService,
    protected loadDataFormsGridRegistrationService: LoadDataFormsGridRegistrationService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected toastService: ToastrService,
    protected errorsFormsService: ErrorsFormsService,
    protected commissioningFormsErrorsService: CommissioningFormsErrorsService,
    protected gridRegistrationService: GridRegistrationRequestService,
    protected createFormsGridRegistrationService: CreateGridRegistrationFormsService,
    protected displayAndSelectService: DisplayAndSelectService
  ) {
    super(authorizationService, dataShareService);
    this.hideAdditionalGridConnectionPointInformationCard$ = this.dataShareService.tenantConfig$.pipe(
      map((config) => !!config.gridRegistration?.hideAdditionalGridConnectionPointInformationCard)
    );
  }

  ngOnInit(): void {}

  loadFormWithModel(result: GridRegistrationModel) {
    this.isAdminUser = this.authorizationService.hasRole('ROLE_EDITOR_GRIDREGISTRATIONS');
    this.selectedTenant = this.dataShareService.selectedTenant;
    this.disableConditions = result.state !== GridRegistrationStatusEnum.Incomplete;
    if (result.storages) {
      result.storages.forEach((x) => {
        this.maxExtractionPower = this.maxExtractionPower + x.usableCapacity * x.count;
      });
    }
    if (result.consumers) {
      result.consumers.forEach((x) => {
        this.maxExtractionPower = this.maxExtractionPower + +x.power * +x.count;
      });
    }
    const sources = [];
    const commonSources = [
      this.httpHapService.get(uri.areaTypes),
      this.httpHapService.get(uri.companyLocationByCurrentTenant),
      this.httpHapService.get(uri.getActiveDevelopmentAreas),
      this.dataShareService.meteringConcepts.meteringConcepts$,
      this.dataShareService.meteringConcepts.storageConcepts$
    ];
    sources.push(...commonSources);

    combineLatest(sources as Observable<any>[])
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe((resArr) => {
        this.areaTypeList = resArr[0];
        this.companyLocationsBytenant = resArr[1];
        this.listDevelopmentAreas = resArr[2];
        this.meteringConceptList = resArr[3];
        this.storageConceptList = resArr[4];

        this.meteringConceptList = this.displayAndSelectService.sortMeteringConceptListBySortPosition(
          this.meteringConceptList
        );
        this.storageConceptList = this.displayAndSelectService.sortMeteringConceptListBySortPosition(
          this.storageConceptList
        );
        this.meteringListToShow = this.displayAndSelectService.meteringConceptToDisplayAndSelect(
          this.meteringConceptList
        );
        this.storageListToShow = this.displayAndSelectService.meteringConceptToDisplayAndSelect(
          this.storageConceptList
        );

        this.companyLocationsBytenant = this.companyLocationsBytenant.filter((x) => x.areatypes.length > 0);
        const elePower = this.areaTypeList[this.areaTypeList.findIndex((x) => x.name == this.areaType)];

        this.companyLocationsBytenant = this.companyLocationsBytenant.filter(
          (ele) => ele.areatypes.filter((eleType) => eleType.id == elePower.id).length > 0
        );

        this.listDevelopmentAreas = this.listDevelopmentAreas.filter((ele) => ele.hasPower);

        this.outToParent.emit({
          areaType: this.areaType
        });
        this.updateFormWithModel(result);

        this.initialised = true;
      });
  }

  selectCompanyLocation(event: any) {
    this.selectedCompanyLocation = event;
    this.loadErrorsList();
  }

  resetCompanyLocation() {
    this.selectedCompanyLocation = null;
    this.selectedCompanyLocationid = null;
    this.objectNotFound = false;
  }

  getModelFromForm(): GridRegistrationModel {
    this.gridRegistration = this.loadDataFormsGridRegistrationService.loadCommonData(
      this.gridRegistration,
      this.selectedGisData,
      this.formGroup
    );
    this.gridRegistration = this.loadDataFormsGridRegistrationService.loadGridRegDataToSave(
      this.technician,
      this.gridRegistration,
      this.selectedCompanyLocation,
      this.formGroup
    );
    this.gridRegistration = this.loadDataFormsGridRegistrationService.resetConnectionObjectAdditionalInfo(
      this.gridRegistration,
      this.formGroup
    );
    return this.gridRegistration;
  }

  updateFormWithModel(result: GridRegistrationModel) {
    // TODO: find a nicer way to do this
    // we want to check if all of the selects have loaded successfully before setting the model
    // otherwise we will have a race-condition between loading the form data and the select-options
    this.disableConditions = result.state != GridRegistrationStatusEnum.Incomplete;

    if (!this.initialised) {
      setTimeout(() => {
        this.updateFormWithModel(result);
      }, 100);
      return;
    }

    this.gridRegistration = result;
    this.gridRegistrationType = result.gridRegistrationType;

    this.selectedGisData = this.loadGisData(
      this.gridRegistration.connectionObject,
      this.gridRegistration.uniqueCoId
    );
    this.selectedCompanyLocation = this.gridRegistration.companyLocation;
    this.selectedCompanyLocationid = this.gridRegistration.companyLocation
      ? this.gridRegistration.companyLocation.id
      : null;
    this.showGetGisData = false;

    this.formGroup = this.createFormsGridRegistrationService.createGridRegistrationForm(
      result ? result : new GridRegistrationModel(),
      this.gridRegistrationType,
      this.maxExtractionPower
    );

    if (this.gridRegistration.state != GridRegistrationStatusEnum.Incomplete) {
      this.formGroup = this.loadDataFormsGridRegistrationService.setAcceptConditionsDisable(this.formGroup);
    }

    if (this.gridRegistration.gridRegistrationType == this.gridRegistrationTypeEnum.Feeder) {
      if (this.gridRegistration.meteringConcept && this.gridRegistration.meteringConcept) {
        this.selectedMeteringConceptToShow = this.displayAndSelectService.selectedMeteringConceptToDisplay(
          this.gridRegistration.meteringConcept.id,
          this.meteringListToShow
        );
      }
      if (this.gridRegistration.hasStorage && this.gridRegistration.storageConcept) {
        this.selectedStorageConceptToShow = this.displayAndSelectService.selectedMeteringConceptToDisplay(
          this.gridRegistration.storageConcept.id,
          this.storageListToShow
        );
      }
    }
    this.canUpdate = this.authorizationService.hasPermission('GRID_REGISTRATION_UPDATE');
    this.disableAllFields =
      this.gridRegistration.state !== this.gridRegistrationStatusEnum.Incomplete &&
      (this.gridRegistration.state === this.gridRegistrationStatusEnum.Requested ||
        (this.gridRegistration.state === this.gridRegistrationStatusEnum.Approved &&
          this.authorizationService.hasOneOfThisRoles(['ROLE_INSTALLATEUR', 'ROLE_ADMIN_INSTALLATEUR'])) ||
        !this.canUpdate) &&
      !this.authorizationService.hasOneOfThisRoles(['ROLE_KEYUSER_GRIDREGISTRATION']);
    if (this.disableAllFields) {
      this.eventsToGis.next('setDisable');
      this.disableFields();
    }
    this.maxExtractionPower = result.extractionNetworkPowerAuthorized;

    this.isDataLoaded = true;
  }

  loadErrorsList() {
    // Warum ist hier diese condition?
    if (this.tryToSubmit) {
      this.errorFormList = this.errorsFormsService.checkTwoLevelFormErrors(
        this.formGroup,
        ErrorFormCommissioningEnum,
        null
      );
      this.errorFormList = this.commissioningFormsErrorsService.updateErrorListAccordingCondition(
        this.selectedCompanyLocation == null,
        'companyLocation',
        this.errorFormList
      );
      // tslint:disable-next-line:triple-equals
      if (this.gridRegistration.gridRegistrationType == this.gridRegistrationTypeEnum.Feeder) {
        if (this.gridRegistration.meteringConcept) {
          this.gridRegistration.meteringConcept =
            Object.keys(this.gridRegistration.meteringConcept).length === 0
              ? null
              : this.gridRegistration.meteringConcept;
        }
        this.errorFormList = this.commissioningFormsErrorsService.updateErrorListAccordingCondition(
          this.gridRegistration.meteringConcept == null,
          'meteringConcept',
          this.errorFormList
        );
      }
      if (this.formGroup.get('hasStorage').value) {
        this.errorFormList = this.commissioningFormsErrorsService.updateErrorListAccordingCondition(
          this.gridRegistration.storageConcept == null,
          'storageConcept',
          this.errorFormList
        );
      } else {
        this.errorFormList = this.commissioningFormsErrorsService.updateErrorListAccordingCondition(
          false,
          'storageConcept',
          this.errorFormList
        );
      }
      this.errorFormList = this.commissioningFormsErrorsService.updateErrorListAccordingCondition(
        this.totalPowerProducers >= this.dataShareService.tenantConfig.gridRegistration.vde4105UpperLimit &&
          !this.hasPvModule,
        'producers.exceedPower',
        this.errorFormList
      );
      this.errorFormList = this.commissioningFormsErrorsService.updateErrorListAccordingCondition(
        this.totalPowerPVModules >= this.dataShareService.tenantConfig.gridRegistration.vde4105UpperLimit &&
          this.hasPvModule,
        'pvModules.exceedPower',
        this.errorFormList
      );

      this.selectedWishDateOK = true;
      this.selectedWishPartDateOK = true;
    }

    console.log('emitting errors', this.errorFormList);
    this.onErrorsChanged.emit(this.errorFormList);
  }

  selectedPVModule(event: any) {
    this.hasPvModule = event.isPv;
    const pvModulesArray = this.formGroup.get('pvModules') as FormArray;
    const producersArray = this.formGroup.get('producers') as FormArray;
    producersArray.clear();
    producersArray.push(this.createFormsGridRegistrationService.createProducerForm(new ProducerModel()));
    if (event.isPv) {
      pvModulesArray.push(this.createFormsGridRegistrationService.createPVModuleForm(new PVModuleModel()));
    } else {
      pvModulesArray.clear();
    }
  }

  producerChangesDetection() {
    this.listProducers = (this.formGroup.get('producers') as FormArray).value;
    this.hasProducers = this.createFormsGridRegistrationService.checkIfHasProducers(this.listProducers);
    if (!this.hasProducers) {
      (this.formGroup.get('storages') as FormArray).clear();
    }
  }

  consumerChangesDetection() {
    this.listConsumers = (this.formGroup.get('producers') as FormArray).value;
  }

  selectedGridRegistrationType($event) {
    switch ($event.action) {
      case AnswerModalTypeEnum.Selected:
        this.selectedMeteringConceptToShow = $event.selectedItem;
        const idx = this.meteringConceptList.findIndex((ele) => ele.id == $event.selectedItem.itemId);
        if (idx != -1) {
          this.gridRegistration.meteringConcept = this.meteringConceptList[idx];
        }
        break;
      case AnswerModalTypeEnum.Reset:
        this.selectedMeteringConceptToShow = null;
        this.gridRegistration.meteringConcept = null;
        break;
    }
  }

  isSelectedStorageConcept($event) {
    switch ($event.action) {
      case AnswerModalTypeEnum.Selected: {
        this.selectedStorageConceptToShow = $event.selectedItem;
        const idx = this.storageConceptList.findIndex((ele) => ele.id == $event.selectedItem.itemId);
        if (idx != -1) {
          this.gridRegistration.storageConcept = this.storageConceptList[idx];
        }
        break;
      }
      case AnswerModalTypeEnum.Reset: {
        this.selectedStorageConceptToShow = null;
        this.gridRegistration.storageConcept = null;
        break;
      }
    }
  }

  isHasStorage($event) {
    this.gridRegistration.hasStorage = $event;
    if (!this.gridRegistration.hasStorage) {
      this.isSelectedStorageConcept({ action: AnswerModalTypeEnum.Reset });
    }
  }

  hasInstalledDevices($event) {
    this.gridRegistration.hasInstalledDevices = $event;
  }

  totalPowerChanges($event) {
    if (!this.hasPvModule) {
      this.totalPowerProducers = $event.power;
      this.formGroup = this.loadDataFormsGridRegistrationService.setValidatorsFeedInManagementInfo(
        this.totalPowerPVModules,
        this.totalPowerProducers,
        this.gridRegistrationType,
        $event,
        this.formGroup
      );
    }
  }

  totalPowerPVModulesChanges($event) {
    this.totalPowerPVModules = $event.power;
    this.hasPvModule = true;
    this.formGroup = this.loadDataFormsGridRegistrationService.setValidatorsFeedInManagementInfo(
      this.totalPowerPVModules,
      this.totalPowerProducers,
      this.gridRegistrationType,
      $event,
      this.formGroup
    );
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }

  getFormGroup(): FormGroup {
    return this.formGroup;
  }

  isValid() {
    if (!this.tryToSubmit) {
      this.detectFormChanges();
      this.tryToSubmit = true;
    }
    this.loadErrorsList();

    return this.errorFormList.length === 0;
  }
}
